import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {Router} from '@angular/router';
import {StoreService} from 'src/app/_services/store.service';
import {AgentAction, AgentPending, StoreAgentService} from 'src/app/_services/store-agent.service';
import {Store} from 'src/app/_models/store';
import {Permission} from 'src/app/_models/permission';
import {PermissionService} from 'src/app/_services/permission.service';

/** ENG-5981: one assistant for the owner — menu, employees, printers, pause, sales.
 *  Changes that need confirmation come back as cards with Confirm / Cancel. */
interface PendingCard extends AgentPending {
  state: 'waiting' | 'running' | 'done' | 'cancelled' | 'failed' | 'undoing' | 'undone';
  note?: string;
  undo_id?: string | null;     // set after Confirm; Undo works for 15 minutes
  undoUntil?: number;
}

const UNDO_MS = 15 * 60 * 1000;

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  actions?: AgentAction[];
  pending?: PendingCard[];
  error?: boolean;
}

@Component({
  selector: 'app-store-agent-chat',
  templateUrl: './store-agent-chat.component.html',
  styleUrls: ['./store-agent-chat.component.scss'],
})
export class StoreAgentChatComponent implements OnInit, OnDestroy {
  @ViewChild('messageContainer') messageContainer: ElementRef;

  showWidget = false;
  isOpen = false;
  isFullscreen = false;
  isLoading = false;
  userInput = '';
  messages: ChatMessage[] = [];
  conversationId: string = null;
  showActivity = false;
  // T8/T9: settings view (manager access + phone links), Master Admin only
  showSettings = false;
  settingsLoading = false;
  settingsError = '';
  grants: { user_id: number; name: string; domains: string[] }[] = [];
  staff: { user_id: number; name: string; role: string | null; has_permission?: boolean }[] = [];
  areaKeys: string[] = [];
  newGrantUser: number | null = null;
  newGrantAreas: { [k: string]: boolean } = {};
  phoneLinks: any[] = [];
  readonly areaLabels: { [k: string]: string } = {
    store: 'Store (pause, hours, sales)', menu: 'Menu', employee: 'Employees', printer: 'Printers',
    delivery: 'Delivery apps', storefront: 'Website',
  };
  // ENG-5981 Phase 3: on the POS screen the assistant opens with a PIN (kept in memory only).
  posPin = '';
  pinInput = '';
  pinError = '';
  private pinTimer: any;
  private readonly PIN_IDLE_MS = 5 * 60 * 1000;
  activity: any[] = [];
  activityLoading = false;
  activityError = '';

  suggestions = [
    'How did we do yesterday?',
    'Pause online orders for 30 minutes',
    'Which items are sold out?',
    'List my employees',
  ];

  private store: Store;
  private storeSub: Subscription;

  constructor(private storeService: StoreService, private agent: StoreAgentService, private router: Router,
              private permissionService: PermissionService) {}

  private permission: Permission | null = null;
  private permSub?: Subscription;

  /** Shown when the store has the feature and this user has 'Labrador Assistant' (ACCESS.ASSISTANT)
   *  in Manage Users > Permissions. On the POS screen the shared login is not the person: there the
   *  staff PIN decides (the backend checks that person's permission). */
  get allowed(): boolean {
    if (!this.store?.getFeatureFlag?.('store_agent')) { return false; }
    if (this.onPos) { return true; }
    return !!this.permission?.hasPermission?.('ACCESS', 'ASSISTANT');
  }

  get onPos(): boolean {
    return (this.router.url || '').startsWith('/home/orders');
  }

  get needsPin(): boolean {
    return this.onPos && !this.posPin;
  }

  submitPin() {
    const pin = (this.pinInput || '').trim();
    if (!pin) { return; }
    this.posPin = pin;
    this.pinInput = '';
    this.pinError = '';
    this.touchPin();
  }

  private touchPin() {
    clearTimeout(this.pinTimer);
    if (this.posPin) { this.pinTimer = setTimeout(() => this.lock(), this.PIN_IDLE_MS); }
  }

  private lock(message = '') {
    this.posPin = '';
    this.pinError = message;
    clearTimeout(this.pinTimer);
  }

  private pinFor(): string | undefined {
    if (!this.onPos) { return undefined; }
    this.touchPin();
    return this.posPin || undefined;
  }

  private onPinRejected(err: any): boolean {
    if (this.onPos && err?.status === 403) {
      this.lock(err?.error?.detail === 'Wrong PIN.' ? 'Wrong PIN.' : (err?.error?.detail || 'Not allowed.'));
      return true;
    }
    return false;
  }

  ngOnInit() {
    this.storeSub = this.storeService.current.subscribe(store => {
      if (!store) { return; }
      const changed = this.store && String(this.store.id) !== String(store.id);
      this.store = store;
      this.showWidget = this.allowed;
      if (changed) { this.reset(); }
    });
    this.permSub = this.permissionService.current.subscribe(permission => {
      this.permission = permission;
      this.showWidget = this.allowed;
    });
  }

  ngOnDestroy() {
    clearTimeout(this.pinTimer);
    clearInterval(this.waitTimer);
    document.body.classList.remove('ai-chat-fullscreen');
    this.storeSub?.unsubscribe();
    this.permSub?.unsubscribe();
  }

  reset() {
    this.showActivity = false;
    this.showSettings = false;
    this.messages = [];
    this.conversationId = null;
    if (this.isOpen) { this.welcome(); }
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.lock();
      this.isFullscreen = false;
      document.body.classList.remove('ai-chat-fullscreen');
    } else if (this.messages.length === 0) {
      this.welcome();
    }
    this.scrollToBottom();
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    document.body.classList.toggle('ai-chat-fullscreen', this.isFullscreen);
    this.scrollToBottom();
  }

  private welcome() {
    this.messages.push({
      role: 'assistant',
      text: `Hi! I can help with ${this.store?.store_name || 'your store'}: sales, pausing online orders, ` +
        'the menu, employees and printers. Bigger changes always ask you to confirm first.',
    });
  }


  // T11: tell the user what is happening while the model works (real models take 5-15 s).
  waitText = '';
  private waitTimer: any;

  private startWait() {
    const started = Date.now();
    const tick = () => {
      const s = (Date.now() - started) / 1000;
      this.waitText = s < 8 ? 'Thinking…' : s < 25 ? 'Checking your store…' : 'Taking longer than usual…';
    };
    tick();
    clearInterval(this.waitTimer);
    this.waitTimer = setInterval(tick, 1000);
  }

  private stopWait() {
    clearInterval(this.waitTimer);
    this.waitText = '';
  }

  sendMessage(text?: string) {
    const message = (text || this.userInput || '').trim();
    if (!message || this.isLoading || !this.store) { return; }
    this.messages.push({role: 'user', text: message});
    this.userInput = '';
    this.isLoading = true;
    this.startWait();
    this.scrollToBottom();

    this.agent.chat(this.store.id, {message, conversation_id: this.conversationId}, this.pinFor()).subscribe({
      next: res => {
        this.conversationId = res.conversation_id;
        this.messages.push({
          role: 'assistant',
          text: res.message,
          actions: (res.actions || []).filter(a => !this.isPending(a)),
          pending: (res.pending || []).map(p => ({...p, state: 'waiting' as const})),
        });
        this.isLoading = false;
        this.stopWait();
        this.scrollToBottom();
      },
      error: err => {
        this.stopWait();
        if (this.onPinRejected(err)) { this.isLoading = false; return; }
        this.messages.push({role: 'assistant', text: err?.error?.detail || 'Something went wrong. Please try again.', error: true});
        this.isLoading = false;
        this.scrollToBottom();
      },
    });
  }

  toggleSettings() {
    this.showSettings = !this.showSettings;
    this.showActivity = false;
    if (this.showSettings) { this.loadSettings(); }
  }

  loadSettings() {
    if (!this.store) { return; }
    this.settingsLoading = true;
    this.settingsError = '';
    this.agent.grants(this.store.id).subscribe({
      next: res => {
        this.grants = res.grants || [];
        this.staff = res.staff || [];
        this.areaKeys = res.domains || [];
        this.settingsLoading = false;
      },
      error: err => {
        this.settingsLoading = false;
        this.settingsError = err?.status === 403 ? 'Only a Master Admin can change who uses the assistant.'
          : (err?.error?.detail || 'Could not load settings.');
      },
    });
    this.agent.links(this.store.id).subscribe({next: rows => this.phoneLinks = rows || [], error: () => this.phoneLinks = []});
  }

  noPermission(userId: number): boolean {
    return this.staff.find(s => s.user_id === userId)?.has_permission === false;
  }

  staffWithoutAccess() {
    const has = new Set(this.grants.map(g => g.user_id));
    return this.staff.filter(s => !has.has(s.user_id));
  }

  toggleGrantArea(g: { user_id: number; domains: string[] }, area: string) {
    const domains = g.domains.includes(area) ? g.domains.filter(d => d !== area) : [...g.domains, area];
    if (!domains.length) { this.removeGrant(g); return; }
    this.agent.saveGrant(this.store.id, g.user_id, domains).subscribe({
      next: res => g.domains = res.domains,
      error: err => this.settingsError = err?.error?.detail || 'Could not save.',
    });
  }

  addGrant() {
    const domains = Object.keys(this.newGrantAreas).filter(k => this.newGrantAreas[k]);
    if (!this.newGrantUser || !domains.length) { return; }
    this.agent.saveGrant(this.store.id, this.newGrantUser, domains).subscribe({
      next: () => { this.newGrantUser = null; this.newGrantAreas = {}; this.loadSettings(); },
      error: err => this.settingsError = err?.error?.detail || 'Could not save.',
    });
  }

  removeGrant(g: { user_id: number }) {
    this.agent.removeGrant(this.store.id, g.user_id).subscribe({
      next: () => this.grants = this.grants.filter(x => x.user_id !== g.user_id),
      error: err => this.settingsError = err?.error?.detail || 'Could not remove.',
    });
  }

  revokeLink(link: any) {
    this.agent.revokeLink(this.store.id, link.id).subscribe({
      next: () => link.revoked = true,
      error: err => this.settingsError = err?.error?.detail || 'Could not turn the link off.',
    });
  }

  grantName(userId: number): string {
    return this.grants.find(g => g.user_id === userId)?.name || '';
  }

  toggleActivity() {
    this.showSettings = false;
    this.showActivity = !this.showActivity;
    if (this.showActivity) { this.loadActivity(); }
  }

  loadActivity() {
    if (!this.store) { return; }
    this.activityLoading = true;
    this.activityError = '';
    this.agent.logs(this.store.id, 100).subscribe({
      next: rows => { this.activity = (rows || []).filter(r => r.tool); this.activityLoading = false; },
      error: err => { this.activityError = err?.error?.detail || 'Could not load the activity.'; this.activityLoading = false; },
    });
  }

  statusLabel(status: string): string {
    return ({ok: 'Done', confirmed: 'Done (confirmed)', pending: 'Waiting', refused: 'Not allowed',
      error: 'Failed', dry_run: 'Preview only'} as any)[status] || status;
  }

  confirm(card: PendingCard) {
    if (card.state !== 'waiting') { return; }
    card.state = 'running';
    this.agent.confirm(this.store.id, card.pending_id, this.pinFor()).subscribe({
      next: res => {
        card.state = 'done';
        card.undo_id = res?.undo_id || null;
        card.undoUntil = Date.now() + UNDO_MS;
        card.note = card.undo_id ? 'Done. You can undo this for 15 minutes.' : (card.undo_note || 'Done.');
      },
      error: err => { card.state = 'failed'; card.note = err?.error?.detail || 'That did not work.'; },
    });
  }

  canUndoCard(card: PendingCard): boolean {
    return card.state === 'done' && !!card.undo_id && (card.undoUntil || 0) > Date.now();
  }

  undoCard(card: PendingCard) {
    if (!this.canUndoCard(card)) { return; }
    card.state = 'undoing';
    this.agent.undo(this.store.id, card.undo_id as string, this.pinFor()).subscribe({
      next: () => { card.state = 'undone'; card.undo_id = null; card.note = 'Undone. Everything is back as it was.'; },
      error: err => { card.state = 'done'; card.undo_id = null; card.note = err?.error?.detail || 'Could not undo. Please fix it in the console.'; },
    });
  }

  sendToPhone() {
    if (!this.store || this.isLoading) { return; }
    this.isLoading = true;
    this.agent.sendPhoneLink(this.store.id).subscribe({
      next: res => {
        this.isLoading = false;
        this.messages.push({role: 'assistant', text: res.sms_error
          ? `I could not text the link (${res.sms_error}). Open this on your phone instead:\n${res.url}`
          : `I texted a link to ${res.sent_to}. It works for 30 days on the first phone that opens it.`});
        this.scrollToBottom();
      },
      error: err => {
        this.isLoading = false;
        this.messages.push({role: 'assistant', text: err?.error?.detail || 'Could not create the phone link.', error: true});
        this.scrollToBottom();
      },
    });
  }

  undo(action: AgentAction) {
    if (!action.undo_id || action.undoState) { return; }
    action.undoState = 'running';
    this.agent.undo(this.store.id, action.undo_id, this.pinFor()).subscribe({
      next: () => { action.undoState = 'done'; action.undoNote = 'Undone.'; },
      error: err => { action.undoState = 'failed'; action.undoNote = err?.error?.detail || 'Could not undo.'; },
    });
  }

  cancel(card: PendingCard) {
    if (card.state === 'waiting') {
      card.state = 'cancelled';
      card.note = 'Cancelled. Nothing was changed.';
    }
  }

  previewText(card: PendingCard): string {
    const p = card.preview;
    if (!p) { return this.toolLabel(card.tool); }
    if (typeof p === 'string') { return p; }
    if (p.preview) { return typeof p.preview === 'string' ? p.preview : JSON.stringify(p.preview); }
    if (p.message) { return p.message; }
    return `${this.toolLabel(card.tool)}: ${JSON.stringify(card.args)}`;
  }

  toolLabel(tool: string): string {
    return (tool || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  /** Cards to show: when the turn changed something, the lookups before it are noise. */
  shownActions(msg: ChatMessage): AgentAction[] {
    const actions = msg.actions || [];
    const writes = actions.filter((a: any) => !/^(search_|get_|list_)/.test(a.tool));
    if (writes.length) { return writes; }
    // searched, then looked one up: the detail card is enough
    const details = actions.filter((a: any) => /^get_/.test(a.tool) && !(a.result && a.result.error));
    return details.length ? details : actions;
  }

  actionFailed(a: AgentAction): boolean {
    return !!(a?.result && typeof a.result === 'object' && a.result.error);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  formatMessage(text: string): string {
    if (!text) { return ''; }
    let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    html = html.replace(/\n/g, '<br>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return html.replace(/^(<br>)+/, '');
  }

  private isPending(a: AgentAction): boolean {
    return a?.result && typeof a.result === 'object' && a.result.status === 'needs_confirmation';
  }

  private scrollToBottom() {
    setTimeout(() => {
      const el = this.messageContainer?.nativeElement;
      if (el) { el.scrollTop = el.scrollHeight; }
    }, 50);
  }
}
